import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, Inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { EditorModule } from '@progress/kendo-angular-editor';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { NotificationModule } from '@progress/kendo-angular-notification';
import { LabelModule } from '@progress/kendo-angular-label';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { ControlHost, Resource } from '../../models/models.lib';
import { FormModes } from '../../common/constants.lib';
import { LoggerService } from '../../shared/logger/logger.service';
import {
  SelectEvent,
  RemoveEvent,
  ErrorEvent,
  CancelEvent,
  PauseEvent,
  ResumeEvent,
  SuccessEvent,
  UploadEvent,
  UploadProgressEvent,
  FileInfo,
} from "@progress/kendo-angular-upload";
import { CheckboxListComponent } from '../../common-components/checkbox-list/checkbox-list.component';
import { UploadsModule } from '@progress/kendo-angular-upload';
import { AppConfig, CONFIG_TOKEN } from '../../setup/config';
import { FileRestrictions } from "@progress/kendo-angular-upload";
import { SecurityService } from '../../auth/security.service';
import { environment } from '../../../environments/environment';

const { apiUrl } = environment;

@Component({
  selector: 'casi-edit-resource',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule, ReactiveFormsModule,
    FontAwesomeModule,
    RouterLink, RouterLinkActive,
    EditorModule,
    CheckboxListComponent,
    InputsModule,
    ButtonsModule,
    NotificationModule,
    LabelModule,
    DropDownsModule,
    DateInputsModule,
    LayoutModule,
    UploadsModule
  ],
  templateUrl: './edit-resource.component.html',
  styleUrl: './edit-resource.component.scss'
})
export class EditResourceComponent implements OnInit, OnDestroy {

  @Input({ required: true }) resource: Resource = new Resource();
  @Input({ required: true }) hostInfo: ControlHost = new ControlHost();
  @Input({ required: true }) mode: FormModes = FormModes.NOTSET;

  @Output() onCancel = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<any>();

  public value = '';
  public uploadSaveUrl: string = "fileupload";

  form: FormGroup;

  public linkTypeIsDocument: boolean = true;
  public showUrl: boolean = true;

  private _subs: Subscription[] = [];

  myRestrictions: FileRestrictions = {
    allowedExtensions: [".pdf"],
    maxFileSize: 20971520
  };

  constructor(
    private fb: FormBuilder,
    private logger: LoggerService,
    @Inject(CONFIG_TOKEN) private config: AppConfig,
    private securityService: SecurityService
  ) {
    this.form = this.fb.group(
      {
        id: [null, []],
        title: [null, [Validators.required]],
        description: [null, [Validators.required]],
        url: [null, [Validators.required]],
        document_file_name: [null, []],
        key_words: [null, [Validators.required]],
        linkType: [null, []],
        deleted: [null, []]
      }
    );
  }

  //#region Form properties

  get f() {
    return this.form.controls;
  }
  get description() {
    return this.form.get('description');
  }
  get url() {
    return this.form.get('url');
  }
  get document_file_name() {
    return this.form.get('document_file_name');
  }
  get key_words() {
    return this.form.get('key_words');
  }
  get linkType() {
    return this.form.get('linkType');
  }
  get title() {
    return this.form.get('title');
  }
  get deleted() {
    return this.form.get('deleted');
  }

  //#endregion

  //#region Ng event handlers

  ngOnInit(): void {
    this.logger.debug('ngOnInit...', apiUrl);
    this.form.patchValue(this.resource);
    this.uploadSaveUrl = `${apiUrl}/fileupload?path=resource&uniqueId=${this.resource.id}`
    this._subs.push((this.linkType as AbstractControl).valueChanges.subscribe(lt => {
      this.linkTypeIsDocument = lt === 'Document';
      this.showUrl = (this.resource.url && lt === 'Document') || lt === 'URL';
    }));
    this.linkTypeIsDocument = this.resource.linkType === 'Document';
    this.showUrl = (this.resource.url && this.resource.linkType === 'Document') || this.resource.linkType === 'URL';
    this.securityService.requestCsrfToken();
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnInit...');
    this._subs.forEach(s => s.unsubscribe());
  }

  //#endregion

  onSubmit(): void {
    this.logger.debug(`onSubmit...`, this.form.value, this.mode);
    //  let toSave = Resource.create(this.form.value);
    this.resource.setLists();
    let toSave = { ...this.resource, ...this.form.value };
    this.onSave.emit({
      action: "save",
      data: toSave,
      mode: this.mode
    });
  }

  closeWindow(): void {
    this.logger.debug(`closeWindow...`);
    this.onCancel.emit();
  }

  onRadioChange(args: any): void {
    this.logger.debug(`onRadioChange...`, args);
    this.resource.linkType = args;
    this.resource.url = null as any;
    this.form.patchValue({ url: null });
  }

  //#region Upload event handlers

  public cancel(e: CancelEvent): void {
    this.logger.debug(`cancel event...`, e);
  }

  public clear(): void {
    this.log("clear event");
  }

  public complete(): void {
    this.log(`complete event`);
  }

  public error(e: ErrorEvent): void {
    this.log(`error event: ${e.files[0].name}`);
  }

  public blur(): void {
    this.log(`blur event`);
  }

  public focus(): void {
    this.log(`focus event`);
  }

  public pause(e: PauseEvent): void {
    this.log(`pause event ${e.file.name}`);
  }

  public resume(e: ResumeEvent): void {
    this.log(`resume event ${e.file.name}`);
  }

  public remove(e: RemoveEvent): void {
    this.log(`remove event: ${e.files[0].name}`);
  }

  public select(e: SelectEvent): void {
    this.logger.debug(`select event`, e);
    this.resource.document_file_name = e.files[0].name;
    this.form.patchValue({ document_file_name: e.files[0].name });
  }

  public success(e: SuccessEvent): void {
    let name = e.files[0].name;
    let newId = (e.response as any).body.new_id;
    this.logger.debug(`success event ${name}`, e);
    if (this.mode === FormModes.NEW) {
      this.form.patchValue({ id: newId });
    }
    this.form.patchValue({ url: `${apiUrl}/getfile/${newId}?path=resource/${newId}/${this.resource.document_file_name}` });
  }

  public upload(e: UploadEvent): void {
    this.log(`upload event ${e.files[0].name}`);
    this.securityService.secureUploads(e);
  }

  public uploadProgress(e: UploadProgressEvent): void {
    this.log(`uploadProgress event ${e.percentComplete}`);
  }

  public valueChange(e: FileInfo[]): void {
    this.log(`valueChange event ${e[0].name}`);
  }

  private log(event: string): void {
    this.logger.debug(`log => ${event}`);
  }

  public onGoalChecked(dataItem: any): void {
    this.toggleChecked(dataItem);
    this.logger.debug('onGoalChecked: ', dataItem);
  }

  public onIndustryChecked(dataItem: any): void {
    this.toggleChecked(dataItem);
    this.logger.debug('onIndustryChecked: ', dataItem);
  }

  private toggleChecked(dataItem: any): void {
    this.logger.debug('toggleChecked: ', dataItem);
    dataItem.checked = !dataItem.checked;
  }

  //#endregion

}
